import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';

import { getFetcher, PRODUCT_TYPE, SwButtonSection } from '../../../App';
import { CommonValidationStyle, EmailValidationStyle } from '@pages/Popup/CreateAccount/styles';
import { checkEmail } from '@pages/Popup/CreateAccount';
import { IdValidationStyle, PwValidationStyle } from '@pages/Login/style';
import { TextFileImport } from '@components/molecules';
import { Input, PrimaryButton, Sub1Button } from '@components/atoms';
import { dialogContentsState } from '@components/templates/Dialog';
import { PWD_REGEX, REGEX_WRONG_SPECIAL } from '@variables/utils';
import SWSettingCommonMethods from '../../../utils/settingCommon';
import { submitDebounce } from '../../../utils/lib';
import {
  StyleRegisterLicenseWrap,
  StyleRegisterLicenseForm,
  StyleRegisterLicenseField,
  StyleRegisterLicenseTitle,
  StyleLicenseCode,
  StyleCompanyInfo,
  StyleSubTitle,
  StyleCreateAccount,
} from './style';

const RegisterLicense = () => {
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const confirmRef = useRef<any>(null);
  const [licenseCode, setLicenseCode] = useState('');
  const [certifyLicense, setCertifyLicense] = useState(false);
  const [okDisabled, setOkDisabled] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const { errorHandler } = SWSettingCommonMethods();
  const setDialogContents = useSetRecoilState(dialogContentsState);
  const [validAll, setValidAll] = useState({
    count: 0,
    id: false,
    email: false,
    pwd: false,
    confirmPwd: false,
  });

  const isCloud = PRODUCT_TYPE === 'cloud'; // cloud || onPremise

  /**
   * license certify 동작
   */
  const checkLicenseCode = async () => {
    const url = `api/product/certify`;
    const _params = {
      licenseCode,
    };

    await getFetcher({
      method: 'POST',
      body: _params,
    })(url)
      .then(async (r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: 'License certified successfully.',
            footer: (
              <>
                <PrimaryButton
                  onClick={async () => {
                    setCertifyLicense(true);
                    setDialogContents({ type: 'hidden' });
                  }}
                >
                  OK
                </PrimaryButton>
              </>
            ),
          });
        } else {
          setCertifyLicense(false);
          errorHandler(r);
        }
      })
      .catch((e) => console.log(e, 'error'));
  };

  /**
   * 변경값 저장 하기
   */
  const RegisterLicenseSetting = async () => {
    const url = `api/product/setup`;

    let _userId = userId;

    if (isCloud) _userId = email;

    const _params = {
      companyName,
      email,
      licenseCode,
      password,
      userId: _userId,
    };
    setIsSubmit(true);
    await getFetcher({
      method: 'POST',
      body: _params,
    })(url)
      .then(async (r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: 'License registration succeeded.',
            footer: (
              <>
                <PrimaryButton
                  onClick={async () => {
                    setDialogContents({ type: 'hidden' });
                    location.replace('/');
                  }}
                >
                  OK
                </PrimaryButton>
              </>
            ),
          });
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => console.log(e, 'error'));
  };

  /**
   * id valid
   * @param id
   */
  const checkId = (id: string) => {
    const ID_REGEX = /^([a-z|0-9]+)$/;

    if (!id) {
      return 'none';
    } else if (id.length < 3) {
      return 'short';
    } else if (id.length > 50) {
      return 'invalid';
    } else if (!ID_REGEX.test(id)) {
      return 'regex-invalid';
    }
    return '';
  };
  /**
   * pwd valid
   * @param pwd
   */
  const checkPWD = (id: string, pwd: string) => {
    if (!pwd) return 'none';
    if (id === pwd) {
      return 'sameidpwd';
    } else if (!PWD_REGEX.test(pwd) || REGEX_WRONG_SPECIAL.test(pwd)) {
      return 'regrex';
    }
    return '';
  };

  /**
   * submit 상태 변경
   */
  const checkSubmit = useCallback(() => {
    let valid =
      certifyLicense &&
      password.length > 0 &&
      confirmPassword.length > 0 &&
      companyName.length > 0 &&
      email.length > 0 &&
      password === confirmPassword &&
      !validAll.email &&
      !validAll.pwd &&
      !validAll.confirmPwd;

    if (!isCloud) {
      valid = valid && userId.length > 0 && !validAll.id;
    }

    setOkDisabled(!valid);
  }, [isCloud, certifyLicense, companyName, userId, password, confirmPassword, email, validAll]);

  /**
   * 값 입력후  저장 안하고 페이지 나갈 경우, 이벤트
   * @param ev
   */
  const leave = (ev: any) => {
    ev.preventDefault();
    ev.returnValue = '';

    return (ev.returnValue = '사이트에서 나가시겠습니까? \n\r변경사항이 저장되지 않을 수 있습니다.');
  };

  const historyBack = () => {
    if (!isSubmit && (licenseCode || companyName || userId || password || confirmPassword || email)) {
      if (confirm('사이트에서 나가시겠습니까? \n\r변경사항이 저장되지 않을 수 있습니다.')) {
        location.href = '/';
      }
    } else {
      location.href = '/';
    }
  };

  /**
   * license cerify 상태 초기화
   */
  useEffect(() => {
    setCertifyLicense(false);
  }, [licenseCode]);

  /**
   * blur시 submit 변경 처리
   */
  useEffect(() => {
    checkSubmit();
  }, [validAll]);

  useEffect(() => {
    if (!isSubmit && (licenseCode || companyName || userId || password || confirmPassword || email)) {
      window.addEventListener('beforeunload', leave);
    }
    return function cleanUp() {
      window.removeEventListener('beforeunload', leave);
    };
  }, [companyName, userId, password, confirmPassword, email, licenseCode, isSubmit]);

  return (
    <>
      <StyleRegisterLicenseWrap>
        <StyleRegisterLicenseForm>
          <StyleRegisterLicenseField>
            <StyleRegisterLicenseTitle>Registering a license</StyleRegisterLicenseTitle>
            {/*License Code*/}
            <StyleLicenseCode>
              <StyleSubTitle>
                <strong>License code</strong>
                <span>*Enter the code(copy & paste) or upload the txt file.</span>
              </StyleSubTitle>
              <TextFileImport
                licenseCode={licenseCode}
                setLicenseCode={setLicenseCode}
                onFocus={() => null}
                onBlur={() => null}
              />
              <Sub1Button onClick={submitDebounce(checkLicenseCode)}>Certify</Sub1Button>
            </StyleLicenseCode>

            {/*Company Info */}
            <StyleCompanyInfo>
              <StyleSubTitle>
                <strong>Company Info</strong>
              </StyleSubTitle>
              <Input
                type={'text'}
                placeholder={'Company name'}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                onBlur={() => {
                  let clone = JSON.parse(JSON.stringify(validAll));
                  clone.count = clone.count + 1;
                  setValidAll(clone);
                }}
                disabled={!certifyLicense}
              />
            </StyleCompanyInfo>

            {/*Create Account */}
            <StyleCreateAccount>
              <StyleSubTitle>
                <strong>Create account</strong>
              </StyleSubTitle>
              {!isCloud && (
                <Input
                  type={'text'}
                  placeholder={'ID'}
                  value={userId}
                  onChange={(e) => {
                    e.target.dataset['invalid'] = '';
                    if (e.target.value.length > 50) {
                      setUserId(e.target.value.slice(0, 50));
                      return;
                    }
                    setUserId(e.target.value);
                  }}
                  onBlur={(e) => {
                    const result = checkId(e.target.value);
                    let clone = JSON.parse(JSON.stringify(validAll));
                    if (result) {
                      clone.id = true;
                      setValidAll(clone);
                    } else {
                      clone.id = false;
                      setValidAll(clone);
                    }
                    e.target.dataset['invalid'] = result;
                  }}
                  disabled={!certifyLicense}
                  styles={IdValidationStyle}
                  required
                />
              )}

              <Input
                type={`text`}
                name={`email`}
                placeholder={isCloud ? 'ID(Email address)' : `Email Address`}
                disabled={!certifyLicense}
                value={email}
                onChange={(e) => {
                  e.target.dataset['invalid'] = '';
                  setEmail(e.target.value);
                }}
                styles={css`
                  ${CommonValidationStyle}
                  ${EmailValidationStyle}
                `}
                onBlur={(e) => {
                  let clone = JSON.parse(JSON.stringify(validAll));
                  const result = checkEmail(e.target.value, isCloud);

                  e.target.dataset['invalid'] = result;

                  if (result) {
                    clone.email = true;
                    setValidAll(clone);
                  } else {
                    clone.email = false;
                    setValidAll(clone);
                  }
                }}
                required
              />

              <Input
                className={'pwd'}
                type={'password'}
                placeholder={'Password'}
                value={password}
                onChange={(e) => {
                  e.target.dataset['invalid'] = '';
                  if (e.target.value.length > 50) {
                    setPassword(e.target.value.slice(0, 50));
                    return;
                  }
                  setConfirmPassword('');
                  setPassword(e.target.value);
                }}
                onBlur={(e) => {
                  let clone = JSON.parse(JSON.stringify(validAll));

                  if (!e.target.value) {
                    e.target.dataset['invalid'] = '';
                    clone.pwd = false;
                    setValidAll(clone);
                    return;
                  } else {
                    const result = checkPWD(userId, e.target.value);
                    if (result) {
                      clone.pwd = true;
                      setValidAll(clone);
                    } else {
                      clone.pwd = false;
                      setValidAll(clone);
                    }

                    e.target.dataset['invalid'] = result;
                  }
                }}
                disabled={!certifyLicense}
                styles={PwValidationStyle}
                required
              />
              <Input
                className={'pwd confirmPwd'}
                type={'password'}
                placeholder={'Confirm Password'}
                value={confirmPassword}
                ref={confirmRef}
                data-invalid={password !== confirmPassword ? 'not match' : ''}
                onChange={(e) => {
                  e.target.dataset['invalid'] = '';
                  if (e.target.value.length > 50) {
                    setConfirmPassword(e.target.value.slice(0, 50));
                    return;
                  }
                  setConfirmPassword(e.target.value);
                  return;
                }}
                onBlur={(e) => {
                  let clone = JSON.parse(JSON.stringify(validAll));

                  if (!e.target.value) {
                    e.target.dataset['invalid'] = '';
                    clone.pwd = false;
                    setValidAll(clone);
                    return;
                  } else if (e.target.value !== password) {
                    e.target.dataset['invalid'] = 'not match';
                    clone.pwd = true;
                    setValidAll(clone);
                    return;
                  }
                  setValidAll(clone);
                  e.target.dataset['invalid'] = '';
                }}
                disabled={!certifyLicense}
                styles={PwValidationStyle}
                required
              />
            </StyleCreateAccount>

            {/* back */}
            <PrimaryButton
              styles={css`
                width: 145px;
                height: 48px;
                font-weight: 400;
                font-size: 18px;
                background-color: #555;
                &:hover {
                  background-color: #555;
                }
              `}
              onClick={() => {
                historyBack();
              }}
            >
              Back
            </PrimaryButton>

            {/* Submit */}
            <SwButtonSection>
              <PrimaryButton
                styles={css`
                  width: 145px;
                  height: 48px;
                  font-size: 18px;
                `}
                onClick={submitDebounce(RegisterLicenseSetting)}
                disabled={okDisabled}
              >
                Submit
              </PrimaryButton>
            </SwButtonSection>
          </StyleRegisterLicenseField>
        </StyleRegisterLicenseForm>
      </StyleRegisterLicenseWrap>
    </>
  );
};

export default RegisterLicense;
